import { useSelector } from '@xstate/react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router';
import { UserContext } from '~/app';
import { Button } from '~/components/buttons';
import { ResponseNegotiationModal } from '~/features/market/negotiations/ResponseNegotiationModal';
import { OpportunitiesSidebar } from '~/features/shared/OpportunitiesSidebar';
import { OpportunityRecoveryNotice } from '~/features/shared/OpportunityRecoveryNotice';
import {
  MarketContentPanel,
  MarketEmptyPanel,
  MarketLoadingPanel,
  MarketWorkspaceGrid,
} from '~/features/shared/market-content';
import {
  ResponseUnmaskPanel,
  selectUnmaskViewerScope,
} from '~/features/shared/market-content/ResponseUnmaskPanel';
import { paths } from '~/routing/routes';
import { OpportunityCard } from './sub-components/OpportunityCard';
import { OpportunityDetail } from './sub-components/OpportunityDetail';
import { ResponseDetail } from './sub-components/ResponseDetail';
import { useJobOpportunitiesService } from './useJobOpportunitiesService';

const ZERO_RESPONSE_DESCRIPTION =
  'Jobs appear here after you apply, message an employer, or start a negotiation.';

const getEmptyState = (filter: 'all' | 'active' | 'accepted', totalCount: number) => {
  if (totalCount === 0) {
    return {
      title: 'No job responses',
      description: ZERO_RESPONSE_DESCRIPTION,
      showBrowseAction: true,
    };
  }
  if (filter === 'accepted') {
    return { title: 'No accepted responses', showBrowseAction: false };
  }
  if (filter === 'active') {
    return { title: 'No active responses', showBrowseAction: false };
  }
  return { title: 'No active job responses', showBrowseAction: true };
};

export const JobOpportunities = () => {
  const viewerScopeKey = useSelector(UserContext.useActorRef(), selectUnmaskViewerScope);
  return <ScopedJobOpportunities key={viewerScopeKey} viewerScopeKey={viewerScopeKey} />;
};

const ScopedJobOpportunities = ({ viewerScopeKey }: { viewerScopeKey: string }) => {
  const navigate = useNavigate();
  const [negotiationResponseId, setNegotiationResponseId] = useState<number | null>(null);
  const [negotiationStartDraftIfEmpty, setNegotiationStartDraftIfEmpty] = useState(false);
  const {
    providerId,
    opportunities,
    totalCount,
    selectedOpportunity,
    messages,
    filter,
    includeDeclined,
    loading,
    messagesLoading,
    sendingMessage,
    writing,
    uncertainAction,
    retainedReply,
    replyError,
    handleEditReply,
    sendReceipt,
    canAcknowledgeUncertainty,
    handleReviewUncertainty,
    handleAcknowledgeUncertainty,
    handleDiscardUncertainty,
    error,
    handleSelect,
    handleFilter,
    handleIncludeDeclined,
    handleSendMessage,
    handleAccept,
    handleWithdraw,
    handleReload,
  } = useJobOpportunitiesService();
  const actionsBlocked = loading || messagesLoading || writing || Boolean(uncertainAction);
  const emptyState = error
    ? { title: 'Unable to load job responses', showBrowseAction: false }
    : getEmptyState(filter, totalCount);

  // [MCV-1646] The negotiation editor opens immediately in draft mode; no
  // request is made until Submit Offer. `startFirstNegotiation` -- the option
  // shape OpportunityDetail already sends -- maps straight onto
  // `startDraftIfEmpty`.
  const handleCounter = useCallback(
    (options?: { startFirstNegotiation: boolean }) => {
      if (!selectedOpportunity || actionsBlocked) return;
      setNegotiationStartDraftIfEmpty(options?.startFirstNegotiation ?? false);
      setNegotiationResponseId(selectedOpportunity.responseId);
    },
    [selectedOpportunity, actionsBlocked]
  );

  const handleNegotiationClose = useCallback(() => {
    setNegotiationResponseId(null);
    handleReload();
  }, [handleReload]);

  return (
    <div className="flex min-h-0 flex-1 flex-col p-4 min-[1360px]:p-6">
      {error && <div className="mb-4 p-3 bg-error-50 text-white text-sm rounded-md">{error}</div>}
      <OpportunityRecoveryNotice
        action={uncertainAction}
        busy={loading || messagesLoading || writing}
        canAcknowledge={canAcknowledgeUncertainty}
        onReview={handleReviewUncertainty}
        onAcknowledge={handleAcknowledgeUncertainty}
        onDiscard={handleDiscardUncertainty}
      />

      <MarketWorkspaceGrid
        dataCy="provider-job-opportunities-grid"
        detailKey={selectedOpportunity?.responseId}
        sidebar={
          <OpportunitiesSidebar
            opportunities={opportunities}
            selectedResponseId={selectedOpportunity?.responseId}
            filter={filter}
            includeDeclined={includeDeclined}
            loading={loading}
            title="Job Opportunities"
            emptyState={{
              title: emptyState.title,
              description: emptyState.description,
              action: emptyState.showBrowseAction ? (
                <Button
                  cyTag="browse-job-search"
                  label="Browse Job Search"
                  size="sm"
                  type="button"
                  onClick={() => navigate(paths.hlx.listings)}
                />
              ) : undefined,
            }}
            onSelect={handleSelect}
            onFilter={handleFilter}
            onIncludeDeclined={handleIncludeDeclined}
            renderOpportunity={(opportunity, isSelected, onSelect) => (
              <OpportunityCard
                opportunity={opportunity}
                isSelected={isSelected}
                onSelect={onSelect}
              />
            )}
          />
        }
        detail={
          selectedOpportunity ? (
            <OpportunityDetail
              opportunity={selectedOpportunity}
              onAccept={handleAccept}
              onCounter={handleCounter}
              onWithdraw={handleWithdraw}
              actionsDisabled={actionsBlocked}
              identityControls={
                <ResponseUnmaskPanel
                  responseId={selectedOpportunity.responseId}
                  viewerScopeKey={viewerScopeKey}
                  refreshKey={`${selectedOpportunity.updatedAt}:${messages[messages.length - 1]?.id ?? ''}`}
                  disabled={actionsBlocked}
                  onChanged={handleReload}
                />
              }
            />
          ) : (
            <MarketContentPanel className="h-full" variant="empty">
              {loading ? (
                <MarketLoadingPanel label="Loading opportunities" />
              ) : (
                <MarketEmptyPanel title="Select an opportunity to view details" />
              )}
            </MarketContentPanel>
          )
        }
        conversation={
          selectedOpportunity ? (
            <ResponseDetail
              key={selectedOpportunity.responseId}
              opportunity={selectedOpportunity}
              messages={messages}
              messagesLoading={messagesLoading}
              sendingMessage={sendingMessage}
              sendReceipt={sendReceipt}
              actionsDisabled={actionsBlocked}
              initialDraft={
                retainedReply?.messageBody ??
                (uncertainAction?.responseId === selectedOpportunity.responseId
                  ? uncertainAction.messageBody
                  : undefined)
              }
              onDraftChange={handleEditReply}
              replyError={replyError}
              currentProviderId={providerId}
              onSendMessage={handleSendMessage}
            />
          ) : (
            <MarketContentPanel className="h-full" variant="empty">
              <MarketEmptyPanel title="No opportunity selected" />
            </MarketContentPanel>
          )
        }
      />
      <ResponseNegotiationModal
        open={negotiationResponseId != null}
        responseId={negotiationResponseId}
        responseType="job"
        viewerRole="PROVIDER"
        startDraftIfEmpty={negotiationStartDraftIfEmpty}
        onDraftSubmitted={handleReload}
        onClose={handleNegotiationClose}
      />
    </div>
  );
};
